import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useSelector, useStore } from 'react-redux';
import { useLocation, useNavigationType } from 'react-router';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { LicenseInfo } from '@mui/x-license';
import { setupListeners } from '@reduxjs/toolkit/query';
import { getAuth } from 'firebase/auth';
import { fetchAndActivate, getValue } from 'firebase/remote-config';
import { getRemoteConfig } from 'firebase/remote-config';

import { StyledTextField, SimpleModal, StyledSnackbar, GlobalLoadingSpinner } from '@labradorsports/components';
import { JoinTypes, ProfileFlags, Sites } from '@labradorsports/constants';
import { inIframe, useSite } from '@labradorsports/utils';

import { DocumentsProvider } from './dashboard/documents.js';
import { ReauthenticateModal, ErrorBoundary, AboutModal, ErrorModal } from './main/index.js';
import ContentEditorProvider from './shared/form/content-editor/content-editor-context.js';
import { useDispatcher, useDispatch } from './shared/hooks/index.js';
import { useLogger, ThemeProvider, useNavigateWithQuery } from './shared/providers/index.js';
import { OfflineMonitor } from './shared/structural/index.js';
import {
    RootState,
    mainActions,
    authActions,
    scheduleActions,
    playbookActions,
    scoutingReportActions,
    communityActions,
    Selectors,
    useUpdateRSVPMutation,
    authApi,
    useUpdateProfileFlagMutation,
    useLoadRosterQuery,
    useMyPlaybookQuery,
} from './store/index.js';

const logPage = (location: any) => {
    if (!E2E && !SSR) {
        datadogRum.startView({
            name: location.pathname,
            context: {
                search: location.search,
            },
        });
    }

    if (PROD && DEPLOY) {
        const fullUrl = `${location.pathname}${location.search}`;
        ReactGA.send({ hitType: 'pageview', page: fullUrl });

        window._hsq.push(['setPath', fullUrl]);
        window._hsq.push(['trackPageView']);
    }
};

LicenseInfo.setLicenseKey(
    'f2834110e8c9d369b9bddab77d4cdd5aTz0xMzAyODksRT0xODEzMjc2Nzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI='
);

const AppWrapper: FC = ({ children }) => {
    const dispatch = useDispatch();
    const loginChange = useDispatcher(authActions.LoginChange);
    const setGenericError = useDispatcher(mainActions.GenericError);
    const setGenericAlert = useDispatcher(mainActions.GenericAlert);
    const setReauthenticating = useDispatcher(authActions.Reauthenticating);
    const setPromptRSVPReason = useDispatcher(scheduleActions.SetPromptRSVPReason);
    const [updateRSVP] = useUpdateRSVPMutation();
    const updateFeatureFlags = useDispatcher(mainActions.UpdateFeatureFlags);
    const setNCAAYear = useDispatcher(scoutingReportActions.SetNCAAYear);
    const doShowSignupPrompt = useDispatcher(communityActions.ShowSignupPrompt);
    const doOverrideMenuWithBack = useDispatcher(playbookActions.OverrideMenuWithBack);
    const [setProfileFlag] = useUpdateProfileFlagMutation();

    const store = useStore<RootState>();
    const loading = useSelector((state: RootState) => state.main.loadingCount > 0);
    const genericError = useSelector((state: RootState) => state.main.genericError);
    const genericAlert = useSelector((state: RootState) => state.main.genericAlert);
    const reauthenticating = useSelector((state: RootState) => state.auth.reauthenticating);
    const promptRSVPAddReason = useSelector((state: RootState) => state.schedule.promptRSVPAddReason);
    const showSignupPrompt = useSelector((state: RootState) => state.community.showSignupPrompt);
    const overrideMenuWithBack = useSelector((state: RootState) => state.playbook.overrideMenuWithBack);
    const user = useSelector(Selectors.user);
    const currentUserTeamRole = useSelector(Selectors.currentUserTeamRole);
    const {
        data: { roster },
    } = useLoadRosterQuery();
    const { Config } = useSite();
    const logger = useLogger();

    useMyPlaybookQuery();

    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigateWithQuery();
    const [settingRSVPReason, setSettingRSVPReason] = useState(null);
    const [rsvpReason, setRSVPReason] = useState('');

    useEffect(() => {
        setupListeners(dispatch);

        if (PROD && DEPLOY) {
            window._hsq = window._hsq ?? [];
            ReactGA.initialize(Config.GoogleAnalyticsID);
        }

        window.StonlyWidget('sendData', {
            guideData: {
                production: PROD,
                site: Config.SiteConfig.Site,
                native: APP,
            },
        });

        if (!inIframe()) {
            getAuth().onAuthStateChanged((user) => {
                const state = store.getState();

                loginChange(user);

                if (user) {
                    const loggedIn = state.auth.user;
                    if (user.uid === loggedIn?.uid) {
                        return;
                    }

                    logger.log('logged in', { uid: user.uid });

                    if (PROD && DEPLOY) {
                        ReactGA.set({ userId: user.uid });

                        window._hsq.push([
                            'identify',
                            {
                                email: user.email,
                            },
                        ]);
                    }

                    if (!E2E && !SSR) {
                        datadogRum.setUser({
                            id: user.uid,
                            email: user.email,
                        });
                    }

                    dispatch(authApi.endpoints.loadProfile.initiate());
                }
            });
        }

        const remoteConfig = getRemoteConfig();

        // 12 hours
        remoteConfig.settings.minimumFetchIntervalMillis = 1000 * 60 * 60 * 12;
        remoteConfig.defaultConfig = {
            featureFlags: JSON.stringify({
                christmasEasterEggEnabled: false,
            }),
            ncaaConfig: JSON.stringify({
                [Sites.lacrosselab]: '',
                [Sites.hoopslab]: '',
            }),
        };

        fetchAndActivate(remoteConfig).then(() => {
            const featureFlags = getValue(remoteConfig, 'featureFlags').asString();
            updateFeatureFlags(JSON.parse(featureFlags));

            const ncaaConfig = JSON.parse(getValue(remoteConfig, 'ncaaConfig').asString());
            setNCAAYear(ncaaConfig[Config.SiteConfig.Site]);
        });

        if (!E2E && !SSR) {
            const context = {
                site: Config.SiteConfig.SiteName,
                version: `${VERSION}-${BUILD}`,
                app: APP,
                build: BUILD,
            };

            datadogLogs.setGlobalContextProperty('client', context);
            datadogRum.setGlobalContextProperty('client', context);
        }
    }, []);

    useEffect(() => {
        if (roster.length > 0 && currentUserTeamRole !== JoinTypes.PLAYER) {
            setProfileFlag({ flag: ProfileFlags.INVITED_TEAM, value: true });
        }
    }, [roster]);

    useEffect(() => {
        if (overrideMenuWithBack) {
            doOverrideMenuWithBack(false);
        }

        if (navigationType === 'PUSH' || navigationType === 'POP') {
            logPage(location);
        }
    }, [location]);

    useEffect(() => {
        if (genericAlert) {
            // Automatically dismiss alerts after 5 seconds
            setTimeout(() => {
                setGenericAlert();
            }, 5000);
        }
    }, [genericAlert]);

    const dismissError = () => {
        setGenericError();
        setGenericAlert();
    };

    const cancelReauth = () => {
        setReauthenticating(false);
    };

    const dismissSetRSVPReason = () => setPromptRSVPReason(null);

    const showSetRSVPReason = () => {
        setPromptRSVPReason(null);

        setSettingRSVPReason(promptRSVPAddReason);
    };

    const cancelSetRSVPReason = () => {
        setSettingRSVPReason(null);
        setRSVPReason('');
    };

    const rsvpReasonChange = (evt: any) => {
        setRSVPReason(evt.target.value);
    };

    const saveRSVPReason = async () => {
        await updateRSVP({ eventIds: settingRSVPReason, reason: rsvpReason });

        setSettingRSVPReason(null);
    };

    const declineSignup = () => {
        doShowSignupPrompt(false);
    };

    const goToSignup = () => {
        doShowSignupPrompt(false);

        if (user) {
            navigate('/community/profile');
        } else {
            navigate('/signup');
        }
    };

    const rsvpReasonButtons = [
        {
            text: 'Cancel',
            action: cancelSetRSVPReason,
        },
        {
            text: 'Save',
            action: saveRSVPReason,
            color: 'primary',
        },
    ];

    const signupPromptButtons = [
        {
            text: 'Cancel',
            action: declineSignup,
        },
        {
            text: user ? 'Join Community' : 'Sign Up',
            action: goToSignup,
            color: 'primary',
        },
    ];

    return (
        <ThemeProvider>
            <ContentEditorProvider>
                <ErrorBoundary>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DocumentsProvider>
                            <AboutModal />
                            <SimpleModal onClose={declineSignup} open={showSignupPrompt} buttons={signupPromptButtons}>
                                {user ? (
                                    <>
                                        <p>You need to setup your profile to contribute.</p>
                                        <p>Click below to begin!</p>
                                    </>
                                ) : (
                                    <>
                                        <p>You need to sign up to contribute.</p>
                                        <p>Click below to sign up!</p>
                                    </>
                                )}
                            </SimpleModal>
                            {loading ? <GlobalLoadingSpinner /> : undefined}
                            <ErrorModal open={Boolean(genericError)} onClose={dismissError} error={genericError} />
                            <StyledSnackbar
                                onClose={dismissError}
                                open={genericAlert !== undefined}
                                message={genericAlert}
                            />
                            <StyledSnackbar
                                onClose={dismissSetRSVPReason}
                                open={Boolean(promptRSVPAddReason)}
                                message="Do you want to add a reason for your response?"
                                action={[
                                    <Button key="0" color="secondary" onClick={showSetRSVPReason}>
                                        Add Reason
                                    </Button>,
                                ]}
                            />
                            <SimpleModal
                                onClose={cancelSetRSVPReason}
                                open={Boolean(settingRSVPReason)}
                                buttons={rsvpReasonButtons}
                            >
                                <StyledTextField
                                    label="RSVP Reason"
                                    value={rsvpReason}
                                    onChange={rsvpReasonChange}
                                    fullWidth
                                />
                            </SimpleModal>
                            <ReauthenticateModal open={reauthenticating} onClose={cancelReauth} />
                            <OfflineMonitor />
                            {children}
                        </DocumentsProvider>
                    </LocalizationProvider>
                </ErrorBoundary>
            </ContentEditorProvider>
        </ThemeProvider>
    );
};

export default AppWrapper;
